
import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE_URL } from '../apiBase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUserPermissions = async (baseUser) => {
    if (!baseUser || !baseUser.id) return baseUser;

    try {
      const token = localStorage.getItem('token');
      if (!token) return baseUser;

      const res = await fetch(`${API_BASE_URL}/api/users/${baseUser.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        return baseUser;
      }

      const data = await res.json();
      const items = Array.isArray(data.page_permissions) ? data.page_permissions : [];

      const map = {};
      const keys = [];

      items.forEach((p) => {
        const key = String(p?.page_key || '').trim();
        if (!key) return;
        map[key] = {
          can_create: !!p.can_create,
          can_update: !!p.can_update,
          can_delete: !!p.can_delete,
          can_import: !!p.can_import,
          can_export: !!p.can_export,
        };
        keys.push(key);
      });

      return {
        ...baseUser,
        page_permissions: items,
        page_permissions_map: map,
        merged_page_permissions: keys,
      };
    } catch (error) {
      return baseUser;
    }
  };

  useEffect(() => {
    let intervalId;

    const checkAuth = async (fromInterval = false) => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            const enriched = await loadUserPermissions(data.user);
            setUser(enriched);
          } else {
            let reason = 'Session expired. Please login again.';
            try {
              const data = await res.json();
              if (data && typeof data.error === 'string' && data.error.trim()) {
                reason = data.error.trim();
              }
            } catch {
            }
            if (res.status === 401 || res.status === 403) {
              try {
                localStorage.setItem('logoutReason', reason);
              } catch {
              }
            }
            localStorage.removeItem('token');
            setUser(null);
          }
        } catch (error) {
          localStorage.removeItem('token');
          setUser(null);
        }
      }
      if (!fromInterval) {
        setLoading(false);
      }
    };

    checkAuth(false);

    intervalId = setInterval(() => {
      checkAuth(true);
    }, 10000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  const startLogin = async (email, password) => {
    const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Login failed');
    }

    if (data.otpRequired) {
      return data;
    }

    if (data.token) {
      localStorage.setItem('token', data.token);
      const enriched = await loadUserPermissions(data.user);
      setUser(enriched);
      return { otpRequired: false, user: enriched };
    }

    throw new Error('Unexpected login response');
  };

  const verifyOtp = async (loginId, otp) => {
    const res = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ loginId, otp }),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || 'OTP verification failed');
    }

    localStorage.setItem('token', data.token);
    const enriched = await loadUserPermissions(data.user);
    setUser(enriched);
    return enriched;
  };

  const logout = async () => {
    // Track logout time
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {
          // Ignore errors if logout API fails
        });
      }
    } catch (e) {
      // Ignore errors
    }
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, startLogin, verifyOtp, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const PAGE_KEY_ALIASES = {
  article_creation: ['article_creation', 'articles', 'articlecreation'],
  manual_head_office_transaction: ['manual_head_office_transaction', 'manual_head_office'],
  head_office_report: ['head_office_report', 'daily_head_office_transaction_report'],
  vendor_master: ['vendor_master', 'vendors', 'vendor_management'],
  vendor_followup: ['vendor_followup'],
  vendor_followup_report: ['vendor_followup_report'],
  applications: ['applications', 'user_application_report'],
  applications_add: ['applications_add'],
  role_master: ['role_master'],
  user_permissions: ['user_permissions'],
  page_master: ['page_master'],
  datacenter: ['datacenter'],
  transaction_types: ['transaction_types'],
  code_to_code_purpose: ['code_to_code_purpose'],
  hardcopy_sheet_management: ['hardcopy_sheet_management'],
  hardcopy_sheet_report: ['hardcopy_sheet_report'],
  multi_hardcopy_sheet_report: ['multi_hardcopy_sheet_report'],
  task_management: ['task_management', 'tasks'],
  grid: ['grid'],
  calendar: ['calendar'],
  rtgs: ['rtgs'],
  edit_requests: ['edit_requests'],
  users: ['users'],
  bank_master: ['bank_master'],
  head_master: ['head_master'],
  tax_master: ['tax_master'],
  task_management: ['task_management', 'tasks'],
};

export const hasPageAccess = (user, pageKey) => {
  const role = user?.role || '';
  const isSuperAdmin = role === 'superadmin';
  const permissionsMap = user?.page_permissions_map || {};

  if (pageKey === 'dashboard') return true;
  if (!user) return false;
  if (isSuperAdmin) return true;
  if (!pageKey) return false;

  const aliases = PAGE_KEY_ALIASES[pageKey] || [pageKey];
  for (let i = 0; i < aliases.length; i += 1) {
    const key = aliases[i];
    if (permissionsMap[key]) {
      return true;
    }
  }
  return false;
};

export const usePagePermissions = (pageKeyOrKeys) => {
  const { user } = useAuth();
  const role = user?.role || '';
  const isSuperAdmin = role === 'superadmin';
  const permissionsMap = user?.page_permissions_map || {};

  const keys = Array.isArray(pageKeyOrKeys) ? pageKeyOrKeys : [pageKeyOrKeys];
  let perm = {};
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (key && permissionsMap[key]) {
      perm = permissionsMap[key];
      break;
    }
  }

  const canCreate = isSuperAdmin || Boolean(perm.can_create);
  const canUpdate = isSuperAdmin || Boolean(perm.can_update);
  const canDelete = isSuperAdmin || Boolean(perm.can_delete);
  const canImport = isSuperAdmin || Boolean(perm.can_import);
  const canExport = isSuperAdmin || Boolean(perm.can_export);

  return {
    user,
    role,
    isSuperAdmin,
    permissions: perm,
    canCreate,
    canUpdate,
    canDelete,
    canImport,
    canExport,
  };
};
